// ── Resident domain ───────────────────────────────────────────────────────────

export const GENDER_VALUES = [
  "male",
  "female",
  "other",
  "prefer_not_to_say",
] as const;
export type Gender = (typeof GENDER_VALUES)[number];

export const MOBILITY_LEVEL_VALUES = [
  "ambulatory",
  "walker",
  "wheelchair",
  "bedbound",
] as const;
export type MobilityLevel = (typeof MOBILITY_LEVEL_VALUES)[number];

export const RESIDENT_STATUS_VALUES = [
  "active",
  "inactive",
  "discharged",
  // Set only by the mark-deceased flow (POST /residents/:id/deceased), never a
  // user-selectable status. Included so `ResidentStatus` matches what the DB
  // actually holds.
  "deceased",
] as const;
export type ResidentStatus = (typeof RESIDENT_STATUS_VALUES)[number];

/**
 * Resident console: `residents.level_of_care` — the care tier a resident is
 * placed at. Display labels (e.g. "Assisted Living") are mapped on the frontend;
 * the stored key is stable.
 */
export const LEVEL_OF_CARE_VALUES = [
  "independent",
  "assisted",
  "memory_care",
  "skilled_nursing",
] as const;
export type LevelOfCare = (typeof LEVEL_OF_CARE_VALUES)[number];

/**
 * Resident console: `residents.fall_risk_level`. Stored as a scalar for the
 * header chip + alerts. `none` is an explicit "assessed, not at risk"; `null`
 * means never assessed. True assessment-derivation is a later enhancement —
 * the generic assessment_responses model has no typed fall-risk score yet.
 */
export const FALL_RISK_LEVEL_VALUES = [
  "none",
  "low",
  "moderate",
  "high",
] as const;
export type FallRiskLevel = (typeof FALL_RISK_LEVEL_VALUES)[number];

/**
 * Resident console: `resident_allergies.severity`. Drives the header allergy
 * chip detail (e.g. "Severity: Severe").
 */
export const ALLERGY_SEVERITY_VALUES = [
  "mild",
  "moderate",
  "severe",
  "life_threatening",
] as const;
export type AllergySeverity = (typeof ALLERGY_SEVERITY_VALUES)[number];

// ── Incident domain ───────────────────────────────────────────────────────────

export const INCIDENT_TYPE_VALUES = [
  "fall",
  "medication_error",
  "behavioral",
  "elopement",
  "injury",
  "property_damage",
  /** US-088: auto-created from a refused medication administration. */
  "medication_refusal",
  /** Auto-created from a held medication administration (parity with refusal). */
  "medication_hold",
  /** US-088: auto-created from a facility vital-threshold breach. */
  "vital_sign_alert",
  /** US-088: auto-created from a controlled-substance count discrepancy. */
  "controlled_substance_discrepancy",
  /** US-088: auto-created by the BM-gap sweep (no bowel movement ≥ 5 days). */
  "constipation_alert",
  /** US-091: pattern escalation — ≥3 overdue doses in 7d, or a single overdue controlled-substance dose. */
  "medication_adherence_failure",
  /** US-091: pattern escalation — ≥3 ADL documentation-gap days in 7d. */
  "adl_documentation_failure",
  /** Auto-created when a scheduled dose is administered outside the ±window
   * with no open med-pass session (backlog) — the caregiver acknowledged the
   * late administration instead of being hard-blocked. */
  "medication_late_administration",
  /** US-188: auto-created from a body_map form finding rated high/critical. */
  "skin_integrity_alert",
  /** US-187: ≥2 missed overnight bed-check checkpoints for one resident in one night. */
  "bed_check_documentation_failure",
  "other",
] as const;
export type IncidentType = (typeof INCIDENT_TYPE_VALUES)[number];

export const INCIDENT_SEVERITY_VALUES = [
  "low",
  "medium",
  "high",
  "critical",
] as const;
export type IncidentSeverity = (typeof INCIDENT_SEVERITY_VALUES)[number];

export const INCIDENT_STATUS_VALUES = [
  "reported",
  "under_review",
  "resolved",
  "escalated",
] as const;
export type IncidentStatus = (typeof INCIDENT_STATUS_VALUES)[number];

// ── Care coordination domain (US-088) ─────────────────────────────────────────

/**
 * Polymorphic provenance vocabulary for `origin_type` on incidents, cases, and
 * care plans — names the table the `origin_id` row lives in. Automated incident
 * creation is idempotent on the (origin_type, origin_id) pair.
 *
 * Exception: "trend_alert" (US-088 follow-up). A trend alert is a client-side
 * statistical computation over form/ADL data, not a persisted row, so there is
 * no table for `origin_id` to reference. `origin_id` is the resident's id
 * instead, and the badge this drives on the Trend Alerts card is deliberately
 * coarse — it lights up whenever the resident has ANY case/care plan opened
 * from a trend, not the specific alert that was clicked.
 */
export const RECORD_ORIGIN_TYPE_VALUES = [
  "medication_administration",
  "vital_sign",
  "care_note",
  "controlled_substance_count",
  "trend_alert",
  /** US-188: origin for a skin_integrity_alert incident (createOriginIncident's originType). */
  "form_submission",
] as const;
export type RecordOriginType = (typeof RECORD_ORIGIN_TYPE_VALUES)[number];

/**
 * What a triggered facility vital-threshold rule does: `create_incident`
 * opens a high-severity vital_sign_alert incident; `notify` sends an in-app
 * notification to the manager/medical cohort only.
 */
export const VITAL_THRESHOLD_ACTION_VALUES = [
  "create_incident",
  "notify",
] as const;
export type VitalThresholdAction =
  (typeof VITAL_THRESHOLD_ACTION_VALUES)[number];

// ── Case domain ───────────────────────────────────────────────────────────────
export const CASE_ENTITY_TYPE_VALUES = [
  "resident",
  "facility",
  "employee",
  "group_home",
] as const;
export type CaseEntityType = (typeof CASE_ENTITY_TYPE_VALUES)[number];

export const CASE_PRIORITY_VALUES = [
  "low",
  "medium",
  "high",
  "urgent",
] as const;
export type CasePriority = (typeof CASE_PRIORITY_VALUES)[number];

export const CASE_ACCESS_LEVEL_VALUES = [
  "private",
  "public",
  "strict",
] as const;
export type CaseAccessLevel = (typeof CASE_ACCESS_LEVEL_VALUES)[number];

export const CASE_STATUS_VALUES = [
  "open",
  "in_progress",
  "pending",
  "resolved",
  "closed",
  "cancelled",
] as const;
export type CaseStatus = (typeof CASE_STATUS_VALUES)[number];

// ── Appointment domain ────────────────────────────────────────────────────────

export const APPOINTMENT_TYPE_VALUES = [
  "medical",
  "dental",
  "therapy",
  "social",
  "other",
] as const;
export type AppointmentType = (typeof APPOINTMENT_TYPE_VALUES)[number];

export const APPOINTMENT_STATUS_VALUES = [
  "scheduled",
  "completed",
  "cancelled",
  "no_show",
] as const;
export type AppointmentStatus = (typeof APPOINTMENT_STATUS_VALUES)[number];

/**
 * SCRUM-185: display labels for appointment status — every render site was
 * deriving its own label via `status.replace("_"," ").toUpperCase()`, so
 * "no_show" always read as the raw enum value ("NO SHOW"), which staff found
 * unclear (doesn't say WHO didn't show). Single source of truth so every
 * badge/filter reads "Missed Appointment" without an enum rename — `status`
 * stays "no_show" in the DB/API, this only changes what's displayed.
 */
export const APPOINTMENT_STATUS_LABELS: Record<AppointmentStatus, string> = {
  scheduled: "Scheduled",
  completed: "Completed",
  cancelled: "Cancelled",
  no_show: "Missed Appointment",
};

/**
 * US-190 (SCRUM-185): why an appointment was missed. One list for the picker,
 * the backend Zod schema and the `chk_appointments_missed_reason` CHECK
 * constraint — adding a reason means adding it here AND in a migration.
 * `other` requires a short free-text note (enforced by the schema).
 */
export const MISSED_APPOINTMENT_REASON_VALUES = [
  "resident_refused",
  "family_cancelled",
  "no_reminder",
  "resident_unavailable",
  "provider_schedule_conflict",
  "provider_unavailable",
  "other",
] as const;
export type MissedAppointmentReason =
  (typeof MISSED_APPOINTMENT_REASON_VALUES)[number];

export const MISSED_APPOINTMENT_REASON_LABELS: Record<
  MissedAppointmentReason,
  string
> = {
  resident_refused: "Resident refused",
  family_cancelled: "Family / guardian cancelled",
  no_reminder: "Forgot / no reminder received",
  resident_unavailable: "Resident sleeping / unavailable",
  provider_schedule_conflict: "Provider schedule conflict",
  provider_unavailable: "Provider unavailable / cancelled",
  other: "Other",
};

// ── Shift domain ──────────────────────────────────────────────────────────────

export const SHIFT_TYPE_VALUES = [
  "day",
  "evening",
  "night",
  "on_call",
] as const;
export type ShiftType = (typeof SHIFT_TYPE_VALUES)[number];

/**
 * US-103 — coverage skill-mix positions. A shift definition's `required_positions`
 * declares a per-role minimum keyed by these values; a person counts toward a
 * role via their on-shift RBAC staff role (`role:staff:medical` / `role:staff:care`),
 * NOT the free-form `employees.position` string. The two staff roles are
 * exhaustive for on-shift coverage; managers/admins covering a slot count toward
 * the headcount total but neither skill-mix bucket.
 */
export const COVERAGE_POSITION_VALUES = ["medical", "care"] as const;
export type CoveragePosition = (typeof COVERAGE_POSITION_VALUES)[number];
export const COVERAGE_POSITION_LABELS: Record<CoveragePosition, string> = {
  medical: "Medical",
  care: "Care",
};

/**
 * The single coverage role a definition is restricted to (its `required_positions`
 * holds exactly one position key), or null for an any-role shift. A definition is
 * for ONE role or any — multi-key maps are rejected at validation — so this is the
 * one place backend + frontend agree on "what role does this shift require". Pure,
 * so it lives in the shared package and is imported by both apps (was previously
 * re-implemented in 4 places).
 */
export function coverageRoleOfPositions(
  positions: unknown,
): CoveragePosition | null {
  if (!positions || typeof positions !== "object" || Array.isArray(positions)) {
    return null;
  }
  const keys = Object.keys(positions as Record<string, number>);
  return keys.length === 1 && (keys[0] === "medical" || keys[0] === "care")
    ? (keys[0] as CoveragePosition)
    : null;
}

export const SHIFT_NOTE_TYPE_VALUES = [
  "handoff",
  "incident",
  "general",
  "resident_update",
] as const;
export type ShiftNoteType = (typeof SHIFT_NOTE_TYPE_VALUES)[number];

// ── Care note domain ──────────────────────────────────────────────────────────
// Canonical care_notes.note_type vocabulary. Single source of truth shared by the
// backend CreateCareNoteSchema and both frontend forms (CareNoteForm, CareNotesTab),
// replacing the previously-duplicated/drifted local NOTE_TYPES arrays. Covers every
// value any writer emits: user-authored notes (progress/shift/general) plus the
// internal system writers (appointment completion, incident-sourced notes). NOT
// included: `care_plan_progress`, which is a display-only label synthesized client-side
// from care_plans.progress_notes and is never persisted through the care-notes routes.
export const CARE_NOTE_TYPE_VALUES = [
  "progress",
  "shift",
  "incident",
  "appointment",
  // System-generated reflection of a resident-scoped case note (see the case →
  // care-note handler), parallel to "incident".
  "case",
  // System-generated reflection of a medication administration that carried
  // clinical commentary (observation notes / adverse reaction), parallel to
  // "incident" and "case". Routine doses do NOT reflect.
  "medication",
  "general",
] as const;
export type CareNoteType = (typeof CARE_NOTE_TYPE_VALUES)[number];

/**
 * The subset a HUMAN may file through POST /residents/:id/care-notes.
 *
 * "medication" is deliberately absent. US-101 retired it as a human-authorable
 * type and that decision stands; it lives in CARE_NOTE_TYPE_VALUES only so the
 * medication-administration reflection — written server-side via Prisma, which
 * never passes through this schema — can be labelled and filtered in the UI.
 *
 * Drive both the API enum and the note-type dropdown from this, never from
 * CARE_NOTE_TYPE_VALUES, or the form offers an option the API rejects with 400.
 */
export const AUTHORABLE_CARE_NOTE_TYPE_VALUES = [
  "progress",
  "shift",
  "incident",
  "appointment",
  "case",
  "general",
] as const;
export type AuthorableCareNoteType =
  (typeof AUTHORABLE_CARE_NOTE_TYPE_VALUES)[number];

/** Compile-time guard: the authorable subset can never drift out of the vocabulary. */
const _authorableIsSubset: readonly CareNoteType[] =
  AUTHORABLE_CARE_NOTE_TYPE_VALUES;
void _authorableIsSubset;

// ── Care plan domain ──────────────────────────────────────────────────────────

export const CARE_PLAN_TYPE_VALUES = [
  "medical",
  "behavioral",
  "social",
  "dietary",
  "physical_therapy",
  "occupational_therapy",
  "other",
] as const;
export type CarePlanType = (typeof CARE_PLAN_TYPE_VALUES)[number];

export const CARE_PLAN_STATUS_VALUES = [
  "draft",
  "active",
  "on_hold",
  "completed",
  "cancelled",
] as const;
export type CarePlanStatus = (typeof CARE_PLAN_STATUS_VALUES)[number];

/**
 * Care plans whose review is actively CHASED — the audience for review
 * reminders (`carePlanReviewSweep`) and derived review tasks
 * (`compliance.care_plan_review`).
 *
 * Typed as `CarePlanStatus[]` on purpose. Both call sites previously filtered on
 * `["active", "in_progress"]`, and `in_progress` is a **case** status
 * (`CASE_STATUS_VALUES`) that a care plan can never hold — not in this
 * vocabulary, rejected by the Zod write schema, and zero rows in any
 * environment. It matched nothing, but made the filter read as though it covered
 * more than `active`, and invited "fixing" it by adding further wrong values.
 * Typing the subset turns that class of mistake into a compile error.
 *
 * Deliberately EXCLUDED, each for its own reason:
 *  - `draft`     — not finalized; the work is to finish it, not to review it
 *  - `on_hold`   — paused deliberately; chasing a review defeats the pause
 *  - `completed` / `cancelled` — finished; nothing left to review
 *
 * Note this is narrower than the UI's "still worth displaying an overdue date"
 * rule, which only treats `completed`/`cancelled` as terminal. That gap is
 * intended: a paused plan's passed review date is worth SEEING, without anyone
 * being pinged about it daily.
 */
export const CARE_PLAN_REVIEW_CHASE_STATUSES: readonly CarePlanStatus[] = [
  "active",
];

// ── Insurance domain ──────────────────────────────────────────────────────────

export const INSURANCE_TYPE_VALUES = [
  "primary",
  "secondary",
  "tertiary",
] as const;
export type InsuranceType = (typeof INSURANCE_TYPE_VALUES)[number];

export const SUBSCRIBER_RELATIONSHIP_VALUES = [
  "self",
  "spouse",
  "parent",
  "child",
  "other",
] as const;
export type SubscriberRelationship =
  (typeof SUBSCRIBER_RELATIONSHIP_VALUES)[number];

// ── Form domain ───────────────────────────────────────────────────────────────

export const FORM_FREQUENCY_VALUES = [
  "daily",
  "weekly",
  "monthly",
  "quarterly",
  "annually",
  "on_demand",
] as const;
export type FormFrequency = (typeof FORM_FREQUENCY_VALUES)[number];

/**
 * US-041: cadence enum for `resident_form_requirements.frequency`. Strict
 * superset of FORM_FREQUENCY_VALUES plus `per_shift` (one window per shift
 * instance from the facility's coverage schedule). `on_demand` is excluded
 * from the daily compliance sweep — used for event-driven forms that should
 * never raise an overdue alarm.
 *
 * US-043 adds `one_shot` — a single-due-date cadence with an admission or
 * effective-from anchor (see resident_form_requirements.due_anchor +
 * due_offset_days). One-shot requirements stay visible on the resident
 * profile after fulfillment but are stamped with `fulfilled_at` to exit
 * the sweep's working set.
 */
export const RESIDENT_REQUIREMENT_FREQUENCY_VALUES = [
  "daily",
  "weekly",
  "monthly",
  "quarterly",
  // E2: 6-month cadence (IL CILA §115.240 physician med review / AIMS).
  "semiannually",
  "annually",
  "per_shift",
  "on_demand",
  "one_shot",
  // US-113 Need C: rolling validity window — "filed" iff a submission landed in
  // the last `lookback_days` days (a credential-style validity period, NOT a
  // calendar window). Overdue once the last submission ages past the window.
  "lookback",
] as const;
export type ResidentRequirementFrequency =
  (typeof RESIDENT_REQUIREMENT_FREQUENCY_VALUES)[number];

/**
 * US-043: due-date anchor for `frequency = 'one_shot'` requirements.
 *
 * - `admission` → resolves from `residents.move_in_date`.
 * - `effective_from` → resolves from the requirement row's `effective_from`.
 * - `waiver_services_start` (US-113) → resolves from
 *   `residents.waiver_services_start_date`. Anchors IL CILA deadlines that count
 *   from the start of DD Waiver services (e.g. Personal Plan, 30 days), which is
 *   distinct from physical move-in. Skipped by the sweep when that date is null.
 *
 * ⚠️ Adding a value here is one of FOUR coordinated edits — the two DB CHECK
 * constraints (`resident_form_requirements_due_anchor_check`,
 * `rule_bundle_items_resident_due_anchor_check`), the Zod enums in
 * `backend/src/lib/validation/compliance.ts` (which import this const), and the
 * resolver switch in `residentFormRequirementSweep.ts` must all stay in sync, or
 * an inserted row is rejected / silently never resolves.
 */
export const RESIDENT_REQUIREMENT_DUE_ANCHOR_VALUES = [
  "admission",
  "effective_from",
  "waiver_services_start",
] as const;
export type ResidentRequirementDueAnchor =
  (typeof RESIDENT_REQUIREMENT_DUE_ANCHOR_VALUES)[number];

/**
 * US-041: `resident_form_requirements.source` discriminator. Only `manual`
 * and `care_plan` are written in US-041; `rule_bundle` and `platform_default`
 * are reserved values for US-042 and the platform-baseline follow-up.
 */
export const RESIDENT_REQUIREMENT_SOURCE_VALUES = [
  "manual",
  "care_plan",
  "rule_bundle",
  "platform_default",
] as const;
export type ResidentRequirementSource =
  (typeof RESIDENT_REQUIREMENT_SOURCE_VALUES)[number];

// ── Appointment rule domain ───────────────────────────────────────────────────

/** Matches appointment_type_definitions.code column constraint. */
export const APPOINTMENT_TYPE_CODE_REGEX = /^[a-z][a-z0-9_]{0,59}$/;

/** Matches rule_bundles.code column constraint. */
export const RULE_BUNDLE_CODE_REGEX = /^[a-z][a-z0-9-]{2,59}$/;

// ── ADL domain (US-086) ───────────────────────────────────────────────────────

/**
 * US-086: per-shift ADL self-performance scale (MDS-style) for the five
 * assistance-scored ADL columns on `adl_records`. `refused` = the resident
 * declined the activity; `not_performed` = the activity did not occur this
 * shift (neither implies a care failure — both are valid documentation).
 */
export const ADL_ASSISTANCE_LEVEL_VALUES = [
  "independent",
  "supervision",
  "limited_assistance",
  "extensive_assistance",
  "total_dependence",
  "refused",
  "not_performed",
] as const;
export type AdlAssistanceLevel = (typeof ADL_ASSISTANCE_LEVEL_VALUES)[number];

/**
 * US-086: continence is clinically its own scale (MDS H0300/H0400-style),
 * not an assistance level — `adl_records.continence` uses this vocabulary.
 */
export const ADL_CONTINENCE_LEVEL_VALUES = [
  "continent",
  "occasionally_incontinent",
  "frequently_incontinent",
  "always_incontinent",
  "not_assessed",
] as const;
export type AdlContinenceLevel = (typeof ADL_CONTINENCE_LEVEL_VALUES)[number];

/**
 * US-086 D6: per-shift bowel attestation on `adl_records.bowel_status`
 * (nullable column — `null` means "not assessed" and is neutral for the
 * constipation gap; `no_bm` is an affirmative "none observed this shift"
 * and extends the gap). BM detail lives in `bowel_movements` events.
 */
export const ADL_BOWEL_STATUS_VALUES = ["bm_observed", "no_bm"] as const;
export type AdlBowelStatus = (typeof ADL_BOWEL_STATUS_VALUES)[number];

/** US-086 D6: `bowel_movements.size` vocabulary. */
export const BOWEL_SIZE_VALUES = ["small", "medium", "large"] as const;
export type BowelSize = (typeof BOWEL_SIZE_VALUES)[number];

// ── Medication domain ─────────────────────────────────────────────────────────

/**
 * Category for `medication_orders.discontinue_reason_code` — a structured
 * companion to the free-text `discontinue_reason`. `allergy_adverse_reaction`
 * is the source of truth for the MAR "Allergy" badge and the optional
 * add-to-allergy-list prompt at discontinue time.
 */
export const MEDICATION_DISCONTINUE_REASON_VALUES = [
  "allergy_adverse_reaction",
  "ineffective",
  "completed_course",
  "prescriber_change",
  "resident_request",
  "other",
] as const;
export type MedicationDiscontinueReason =
  (typeof MEDICATION_DISCONTINUE_REASON_VALUES)[number];

/** Display labels for the discontinue-reason picker. */
export const MEDICATION_DISCONTINUE_REASON_LABELS: Record<
  MedicationDiscontinueReason,
  string
> = {
  allergy_adverse_reaction: "Allergy / adverse reaction",
  ineffective: "Ineffective / no longer needed",
  completed_course: "Completed course",
  prescriber_change: "Prescriber change",
  resident_request: "Resident / family request",
  other: "Other",
};

/**
 * Reasons a scheduled dose can be HELD (recorded on the administration). Shared
 * so the dose-outcome picker and the system-generated medication-hold incident
 * speak the same language — the incident description shows the label, never the
 * raw enum key.
 */
export const MEDICATION_HOLD_REASON_VALUES = [
  "npo_status",
  "pending_procedure",
  "adverse_reaction",
  "prescriber_order",
  "drug_interaction",
  "criteria_recommendation",
  "other",
] as const;
export type MedicationHoldReason =
  (typeof MEDICATION_HOLD_REASON_VALUES)[number];

/** Display labels for the hold-reason picker + hold incident descriptions. */
export const MEDICATION_HOLD_REASON_LABELS: Record<
  MedicationHoldReason,
  string
> = {
  npo_status: "NPO (nothing by mouth) status",
  pending_procedure: "Pending procedure / surgery",
  adverse_reaction: "Adverse reaction",
  prescriber_order: "Prescriber order",
  drug_interaction: "Potential drug interaction",
  criteria_recommendation:
    "Criteria recommendation (vitals outside parameters)",
  other: "Other (see notes)",
};

/** Human-readable hold reason; falls back to the raw value for anything not in
 *  the known set (defensive — older free-text rows, future values). */
export function describeHoldReason(reason: string): string {
  return (
    MEDICATION_HOLD_REASON_LABELS[reason as MedicationHoldReason] ?? reason
  );
}

/**
 * Reasons a scheduled dose can be REFUSED. Shared so the dose-outcome picker and
 * the system-generated medication-refusal incident render the same labels.
 */
export const MEDICATION_REFUSAL_REASON_VALUES = [
  "patient_refused",
  "nausea_vomiting",
  "sleeping",
  "away_from_facility",
  "other",
] as const;
export type MedicationRefusalReason =
  (typeof MEDICATION_REFUSAL_REASON_VALUES)[number];

/** Display labels for the refusal-reason picker + refusal incident descriptions. */
export const MEDICATION_REFUSAL_REASON_LABELS: Record<
  MedicationRefusalReason,
  string
> = {
  patient_refused: "Patient refused",
  nausea_vomiting: "Nausea / vomiting",
  sleeping: "Patient sleeping",
  away_from_facility: "Away from facility",
  other: "Other (see notes)",
};

/** Human-readable refusal reason; falls back to the raw value for anything not
 *  in the known set (defensive — older free-text rows, future values). */
export function describeRefusalReason(reason: string): string {
  return (
    MEDICATION_REFUSAL_REASON_LABELS[reason as MedicationRefusalReason] ??
    reason
  );
}

// ── Care plan progress & review (US-100) ──────────────────────────────────────

/**
 * US-100: cadence for `care_plans.review_frequency` — drives `next_review_date`
 * scheduling and the facility "Review Due" queue. Distinct from the free-text
 * care-delivery `frequency` column.
 */
export const CARE_PLAN_REVIEW_FREQUENCY_VALUES = [
  "weekly",
  "biweekly",
  "monthly",
  "quarterly",
  "annually",
] as const;
export type CarePlanReviewFrequency =
  (typeof CARE_PLAN_REVIEW_FREQUENCY_VALUES)[number];

/**
 * US-100: how a care plan's progress bar is measured (`care_plans.progress_mode`).
 * `null` = legacy plan with no declared measure (keeps its stored percentage).
 *  - manual    — a hand-entered point value (label/baseline/target/current).
 *  - auto      — driven by `measure_source` (see MEASURE_SOURCES registry).
 *  - checklist — milestone items; progress = done ÷ total.
 */
export const CARE_PLAN_PROGRESS_MODE_VALUES = [
  "manual",
  "auto",
  "checklist",
] as const;
export type CarePlanProgressMode =
  (typeof CARE_PLAN_PROGRESS_MODE_VALUES)[number];

/**
 * US-100: the clinical data source backing an `auto` measure
 * (`care_plans.measure_source`). Each source declares its kind (point vs rate)
 * in the MEASURE_SOURCES registry (`carePlanMeasures.ts`).
 */
export const CARE_PLAN_MEASURE_SOURCE_VALUES = [
  "vital",
  "adl",
  "medication",
  "incident",
  "appointment",
  "bowel",
] as const;
export type CarePlanMeasureSource =
  (typeof CARE_PLAN_MEASURE_SOURCE_VALUES)[number];

// ── Resident documents ────────────────────────────────────────────────────────
// Type-tiered, access-controlled PHI files attached to a resident. A document's
// `type` determines its sensitivity `tier`, and the tier determines who may open
// it (see TIER_REQUIRED_PERMISSION in ./residentDocumentAccess). Adding a new
// type requires assigning it a tier in DOCUMENT_TYPE_TIER below — the
// Record<ResidentDocumentType, _> type makes that omission a compile error.
export const RESIDENT_DOCUMENT_TYPE_VALUES = [
  // general tier
  "consent_form",
  "correspondence",
  "other",
  // medical tier
  "medical_record",
  "medication_record",
  "assessment",
  // legal tier
  "legal_document",
  "financial",
  "guardianship",
] as const;
export type ResidentDocumentType =
  (typeof RESIDENT_DOCUMENT_TYPE_VALUES)[number];

export const RESIDENT_DOCUMENT_TIER_VALUES = [
  "general",
  "medical",
  "legal",
] as const;
export type ResidentDocumentTier =
  (typeof RESIDENT_DOCUMENT_TIER_VALUES)[number];

/** Each document type's sensitivity tier. Exhaustive by construction. */
export const DOCUMENT_TYPE_TIER: Record<
  ResidentDocumentType,
  ResidentDocumentTier
> = {
  consent_form: "general",
  correspondence: "general",
  other: "general",
  medical_record: "medical",
  medication_record: "medical",
  assessment: "medical",
  legal_document: "legal",
  financial: "legal",
  guardianship: "legal",
};

/** Resolve the tier governing access to a document of the given type. */
export function tierForDocumentType(
  type: ResidentDocumentType,
): ResidentDocumentTier {
  return DOCUMENT_TYPE_TIER[type];
}

// ── Audit domain ──────────────────────────────────────────────────────────────

/**
 * US-139 — the data scopes an auditor's access grant can carry
 * (`audit_access_grants.access_scopes`).
 *
 * A grant narrows WHAT an auditor may read inside a facility they hold access to;
 * the facility itself is authorized separately by the grant's `facility_ids`. Both
 * halves are enforced in ONE place — `withAuth`'s `auditorFacility` +
 * `auditorScopes` gate — and this array is the single vocabulary shared by that
 * gate, the approve-request Zod schema, and the frontend's `hasScope` predicate.
 *
 * Previously these were bare string literals duplicated across ~9 route handlers,
 * which is how the other ~20 audit routes ended up enforcing no scope at all.
 */
// US-159 / Q017 — `schedules` was REMOVED. No route declared it via
// auditorScopes and none checked it in-body, so ticking it authorised nothing
// and declining it protected nothing: a false consent choice, on the one form
// where an organisation decides what an external auditor may read.
//
// `gate_metrics` STAYS. The question's original premise named three unenforced
// scopes and was corrected before the decision — gate_metrics is enforced
// in-body and was confirmed on the wire with a 403, and `employees` is enforced
// at export/[type]:221. Only `schedules` gated nothing.
//
// Stored values are NOT migrated: audit_access_grants.access_scopes is a
// String[] with no DB enum, so a row still carrying "schedules" reads normally
// and nothing acts on it. The column default still emits it (audit.prisma:11) —
// changing that is a decision about what an unspecified grant MEANS, deferred
// with F-099.
export const AUDIT_ACCESS_SCOPE_VALUES = [
  "residents",
  "incidents",
  "forms",
  "employees",
  "gate_metrics",
] as const;
export type AuditAccessScope = (typeof AUDIT_ACCESS_SCOPE_VALUES)[number];

// ── Skin/body assessment domain (US-188) ────────────────────────────────────────

/**
 * US-188: fixed body-region taxonomy for the body_map field type — matches
 * how every other structured field in the form engine works (select/
 * multiselect draw from a fixed option set); free-hand x/y coordinates would
 * be unvalidatable and unqueryable. 21 regions grouped to match a standard
 * front/back/head/feet skin-assessment diagram, with head/feet folded into
 * named zones on the front/back silhouettes rather than 2 extra panels (MVP
 * scope cut — see the US-188 spec §7).
 */
export const BODY_REGION_VALUES = [
  "head",
  "neck",
  "chest",
  "abdomen",
  "groin",
  "left_upper_arm",
  "right_upper_arm",
  "left_forearm",
  "right_forearm",
  "left_hand",
  "right_hand",
  "left_thigh",
  "right_thigh",
  "left_lower_leg",
  "right_lower_leg",
  "back_upper",
  "back_lower",
  "buttocks",
  "left_foot",
  "right_foot",
  "other",
] as const;
export type BodyRegion = (typeof BODY_REGION_VALUES)[number];

/** US-188: skin-finding vocabulary for a body_map entry — no existing
 * CARE_NOTE_TYPE_VALUES or similar vocabulary covers this (confirmed no
 * reuse candidate during the US-188 spec's cycle-1 review). */
export const SKIN_FINDING_TYPE_VALUES = [
  "bruise",
  "redness",
  "rash",
  "wound_laceration",
  "pressure_sore",
  "swelling",
  "discoloration",
  "other",
] as const;
export type SkinFindingType = (typeof SKIN_FINDING_TYPE_VALUES)[number];

// ── Overnight bed-check rounds (US-187) ─────────────────────────────────────────

/** 1:1 with the paper Bed Check form's A/S/W/B/HV/T status codes. */
export const BED_CHECK_STATUS_VALUES = [
  "awake",
  "sleeping",
  "wet",
  "bm",
  "home_visit",
  "toileted",
] as const;
export type BedCheckStatus = (typeof BED_CHECK_STATUS_VALUES)[number];
